/**
 * "No score without evidence" (Task #6807, Story #6757, Roger's request).
 *
 * A novelty or similarity score of 100 beside an EMPTY evidence list reads as "we looked and found
 * nothing", when the analysis may simply not have run (or not finished) — so a perfect score is never
 * shown while there is nothing behind it. The screen says "Analysis not run yet" instead.
 *
 * Only a perfect score is held back: a lower score with no references is a different question (and a
 * rounded 99.96 that the page would print as "100" counts as 100). Unknown evidence (the field is
 * absent, e.g. an older stored report) is NOT treated as empty — we only withhold a score when we can
 * see that the evidence list is there and has nothing in it.
 */
import type { NoveltyReport, PriorArtSummary } from '../types'

export const ANALYSIS_NOT_RUN_LABEL = 'Analysis not run yet'

export const ANALYSIS_NOT_RUN_HINT =
  'A perfect score is not shown until there is evidence behind it. Run the analysis, or wait for it to finish.'

/** True when `scorePct` (0-100) prints as 100 and the evidence list is known to be empty. */
export function isPerfectScoreWithoutEvidence(
  scorePct: number | null | undefined,
  evidenceCount: number | null | undefined,
): boolean {
  if (scorePct == null || evidenceCount == null) return false
  return evidenceCount === 0 && Math.round(scorePct * 10) / 10 >= 100
}

const len = (v: unknown): number => (Array.isArray(v) ? v.length : 0)

/** Evidence behind a project / folder prior-art score: overlap areas, related papers, patent and web matches. */
export function priorArtEvidenceCount(priorArt: PriorArtSummary): number {
  return (
    len(priorArt.overlap_areas) +
    len(priorArt.related_papers) +
    len(priorArt.patent_matches) +
    len((priorArt as { web_matches?: unknown }).web_matches)
  )
}

/** Evidence behind one document's novelty report: its overlap areas plus every claim's prior art. */
export function reportEvidenceCount(report: NoveltyReport): number {
  const claims = Array.isArray(report.claim_analysis) ? report.claim_analysis : []
  return len(report.overlap_areas) + claims.reduce((sum, c) => sum + len(c.prior_art), 0)
}
